<template>
  <div>

    <div class="d-flex justify-space-between mb-12">

      <div>

        <div class="d-flex">

          <month-year-picker
            v-model="toolbarState.yearMonth"
            :before-update="confirmDataLoss"
          />

          <v-select
            class="ml-8"
            :label="$t('titles.velocity_window')"
            density="compact"
            v-model.number="toolbarState.velocityWindow"
            :items="velocityWindowOptions"
            hide-details
          />

        </div>

      </div>

      <div>

        <v-btn
          :text="$t('actions.reset')"
          prepend-icon="$reset"
          :disabled="!stateIsDirty || submitting"
          class="mr-8"
          @click="resetState()"
        />

        <v-btn
          :text="$t('actions.save')"
          prepend-icon="$confirm"
          :disabled="!stateIsDirty || submitting"
          class="mr-8"
          @click="submit"
        />

        <v-btn-toggle v-model="mode" mandatory density="compact">
          <v-btn value="list" :text="$t('titles.list')" />
          <v-btn value="calendar" :text="$t('titles.calendar')" />
        </v-btn-toggle>

      </div>

    </div>

    <busy-panel :busy="loadingLocationData">

      <v-alert v-if="!locationProducts" color="info">
        <p>This location has no Airport LPs.</p>
      </v-alert>

      <v-tabs-window v-if="locationProducts" v-model="mode">

        <v-tabs-window-item value="list">

          <uri-list
            :context="context"
            :state="state"
            :busy="busy"
            @bulk-edit-cost="(lpId: number) => openCostModal(lpId)"
            @bulk-edit-inventory="(lpId: number) => openInventoryModal(lpId)"
            @edit-default-rates="(lpId: number) => openDefaultRatesModal(lpId)"
          />

        </v-tabs-window-item>

        <v-tabs-window-item value="calendar">

          <uri-calendar
            :context="context"
            :state="state"
            :year-month="toolbarState.yearMonth"
            :busy="busy"
          />

        </v-tabs-window-item>

      </v-tabs-window>

    </busy-panel>

    <v-fab app icon="$confirm" :disabled="!stateIsDirty || submitting" @click="submit" />

  </div>
</template>

<script setup lang="ts">

import { computed, onBeforeMount, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { useTitle } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

// components
import MonthYearPicker from '../../../forms/MonthYearPicker.vue'
import BusyPanel from '../../../BusyPanel.vue'
import UriList from './UriList.vue'
import UriCalendar from './UriCalendar.vue'
import CostModal from './CostModal.vue'
import DefaultRatesModal from './DefaultRatesModal.vue'
import InventoryModal from './InventoryModal.vue'
import ConfirmResetModal from './ConfirmResetModal.vue'

// models
import AuditLog from '../../../../models/AuditLog'
import { Inventory, type UpdateInventoryByLocationIdRequest } from '../../../../models/Inventory'
import Location from '../../../../models/Location'
import LocationProduct from '../../../../models/LocationProduct'
import { DefaultRate, type GetRatesByLocationIdResponse } from '../../../../models/DefaultRate'
import InventoryApi, { type GetInventoriesResponse } from '../../../../services/api-clients/InventoryApi'
import RatesApi, { type SyncSpotRatesRequest, type SyncSpotRatesRequestItem } from '../../../../services/api-clients/RatesApi'

// misc
import _, { range } from 'underscore'
import NotificationService from '../../../../services/NotificationService'
import { Temporal } from '@js-temporal/polyfill'
import type { UriContext, UriDateEntry, UriLpContext, UriLpEntry, UriState } from './types'
// @ts-ignore
import { openDialog } from 'vue3-promise-dialog'
import { onBeforeRouteLeave } from 'vue-router'
import { ReportingApi, type ReportingGetActivityResponse, type GetVelocityResponse } from '../../../../services/api-clients/ReportingApi'
import { type ChannelDefaultRateFieldState, Weekday } from '../../../../types/misc'
import { userUriPermissionChecker } from '../../../../utils/useUriPermissionChecker'

interface Props {
  location: Location
}

const props = defineProps<Props>()

const { t } = useI18n()
const { uriPermittedForLocation } = userUriPermissionChecker()
const title = useTitle()
title.value = t('titles.uri')

const router = useRouter()

onBeforeMount(() => {
  if (!uriPermittedForLocation(props.location.ID)) {
      NotificationService.info('Access to URI denied')
      router.replace({name: 'locationDetail', params: {id: props.location.ID}})
  }
})

const busy = computed<boolean>(() => loadingLocationData.value || loadingDateRangeData.value || submitting.value)

/**
 * Airport LPs for the selected location
 */

const locationProducts = computed<LocationProduct[]>(() => {
  return LocationProduct.query().where('locationID', props.location.ID)
    .whereHas('product', (query) => {
      query.where('verticalType', 'airport')
    })
    .with(['product',  'channelFacilities'])
    .get()
})

/**
 * Toolbar
 */

interface ToolbarState {
  yearMonth: Temporal.PlainYearMonth
  velocityWindow: number
}

const toolbarState = reactive<ToolbarState>({
  yearMonth: Temporal.Now.plainDateTimeISO().toPlainYearMonth(),
  velocityWindow: 24,
})

const velocityWindowOptions = [ // TODO: make dynamic?
  {value: 12, title: t('titles.velocity_duration_in_hours', 12)},
  {value: 24, title: t('titles.velocity_duration_in_hours', 24)},
  {value: 72, title: t('titles.velocity_duration_in_hours', 72)},
]

const mode = ref('list')

const selectedDateRange = computed<{start: Temporal.PlainDate, end: Temporal.PlainDate}>(() => {
  const start = new Temporal.PlainDate(toolbarState.yearMonth.year, toolbarState.yearMonth.month, 1)
  const end = new Temporal.PlainDate(toolbarState.yearMonth.year, toolbarState.yearMonth.month, toolbarState.yearMonth.daysInMonth)
  return {start, end}
})

const dates = computed<Temporal.PlainDate[]>(() => {
  const numDays = selectedDateRange.value.start.until(selectedDateRange.value.end).days + 1
  return range(0, numDays).map(i => selectedDateRange.value.start.add({days: i}))
})

watch(selectedDateRange, () => {
  loadDataForSelectedDateRange()
})

onBeforeRouteLeave(async (): Promise<boolean> => {
  return confirmDataLoss().then(() => true).catch(() => false)
})

function confirmDataLoss(): Promise<void> {

  if (!stateIsDirty.value) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    openDialog(ConfirmResetModal).then((result: undefined | boolean) => result ? resolve() : reject())
  })

}

/**
 * Context
 */

const context = computed<UriContext>(() => {
  return {
    velocityWindow: toolbarState.velocityWindow,
    locationSpacesTotal: props.location.onlineStalls,
    lps: locationProducts.value.map(lp => {

      const channelFacilities = new Map()

      lp.channelFacilities.forEach(cf => {
        channelFacilities.set(cf.ID, cf)
      })

      const dr = structuredRates.value.get(lp.ID)

      const cdrMap = new Map()
      if (dr) {
        dr.channelDefaultRates.forEach(cdr => {
          cdrMap.set(cdr.channelFacilitiesID, {cdrId: cdr.ID, channelCost: cdr.channelCost})
        })
      }

      const lpContext: UriLpContext = {
        lpId: lp.ID,
        name: lp?.product.name ?? '(unknown product)',
        vertical: lp?.product.verticalType,
        channelFacilities: channelFacilities,
        defaultRate: {
          // @ts-ignore
          drId: dr?.defaultRate.ID,
          // @ts-ignore
          defaultCost: dr?.defaultRate.defaultCost,
          channelDefaultRates: cdrMap,
        }
      }

      return lpContext

    }),
  }
})

/**
 * Raw inventory and rate records for the selected month
 */

const inventories = ref<GetInventoriesResponse>({inventory: [], blackouts: {}})
const rates = ref<GetRatesByLocationIdResponse>({default_rates: [], spot_rates: [], channel_default_rates: [], inventories_with_event_tiers: null})
const activity = ref<ReportingGetActivityResponse>({})
const velocity = ref<GetVelocityResponse>(new Map())

type StructuredRates = Map<
  number,
  StructuredRatesItem
>

interface StructuredRatesItem {
  defaultRate: GetRatesByLocationIdResponse['default_rates'][0]
  channelDefaultRates: GetRatesByLocationIdResponse['channel_default_rates']
  spotRates: GetRatesByLocationIdResponse['spot_rates']
}

const structuredRates =  computed<StructuredRates>(() => {

  const map: StructuredRates = new Map()

  rates.value.default_rates.forEach(dr => {

    const item: StructuredRatesItem = {
      defaultRate: dr,
      channelDefaultRates: rates.value.channel_default_rates.filter(cdr => cdr.defaultRateID === dr.ID),
      spotRates: rates.value.spot_rates.filter(sr => sr.defaultRateID === dr.ID),
    }

    map.set(dr.locationProductID, item)

  })

  return map

})

// const loadingDateRangeData = ref<boolean>(false)
const loadingDateRangeData = computed<boolean>(() => {
  return pendingRequestAbortControllers.value.length > 0
})
const pendingRequestAbortControllers = ref<AbortController[]>([])

// let correlationId: null | string = null
let monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']

function loadDataForSelectedDateRange(): Promise<void> {

  pendingRequestAbortControllers.value.forEach((controller) => {
    controller.abort()
  })

  const abortController = new AbortController()
  pendingRequestAbortControllers.value.push(abortController)

  const {start, end} = selectedDateRange.value

  const loadInventory = InventoryApi.getInventories(props.location.ID, start.toString(), end.toString(), abortController.signal)
    .then(response => inventories.value = response)

  const loadRates = DefaultRate
    .getByLocationId({locationId: props.location.ID, vertical: 'airport', start: start, end: end, signal: abortController.signal})
    .then(response => rates.value = response)

  const loadActivity = ReportingApi.getActivity([props.location.ID], start, end, abortController.signal)
    .then(response => activity.value = response)

  const loadVelocity = ReportingApi.getVelocity(new Set([props.location.ID]), start, end, undefined, 'airport', abortController.signal)
    .then(response => velocity.value = response)

  return Promise.all([loadInventory, loadRates, loadActivity, loadVelocity])
    .catch(e => {
      if (axios.isCancel(e)) {
        return
      }
      console.error(e)
      NotificationService.error('Couldn\'t load inventory and rate data!')
    })
    .then(() => {
      populateState() // TODO: do we really want to do this here?

      // correlationId = crypto.randomUUID()

      AuditLog.write({
        targetType: 'location',
        targetID: props.location.ID,
        action: 'read',
        otherAction: 'uri_page_view',
        locationID: props.location.ID,
        organizationID: props.location.organizationID,
        actionDetails: {
          month: monthNames.at(toolbarState.yearMonth.month - 1),
          year: toolbarState.yearMonth.year,
          // correlationID: correlationId,
        },
      })
    })
    .finally(() => {
      pendingRequestAbortControllers.value.splice(pendingRequestAbortControllers.value.indexOf(abortController), 1)
    })

}

/**
 * Initialization
 */

const loadingLocationData = ref<boolean>(false)

onBeforeMount(() => {

  loadDataForSelectedDateRange()

  loadingLocationData.value = true

  Promise
    .all([
      LocationProduct.fetchByLocationID(props.location.ID),
    ])
    .catch((e) => {
      console.error(e)
      NotificationService.error('Couldn\'t load location data!')
    })
    .finally(() => {
      loadingLocationData.value = false
    })

})

/**
 * Main state
 */

const state = reactive<UriState>({dates: []})

function populateState() {

  const today = Temporal.Now.plainDateISO()

  state.dates = dates.value.map(date => {

    const dateString = date.toString()

    const activityEntry = dateString in activity.value ? activity.value[dateString] : null

    let locationSpacesSold = null, locationSpacesRatio = null

    if (activityEntry) {
      locationSpacesSold = activityEntry.max_on_lot.total
      locationSpacesRatio = locationSpacesSold / props.location.onlineStalls
    }

    const isoDate = date.toString()
    const disabled = Temporal.PlainDate.compare(date, today) === -1

    const lpEntries: UriLpEntry[] = []

    locationProducts.value.forEach(lp => {

      const dr = structuredRates.value.get(lp.ID)

      if (!dr) {
        console.error(`Couldn\'t find default rates for LP ${lp.ID}`)
        return
      }

      const inventory = inventories.value.inventory.find(i => i.locationProductID === lp.ID && i.date.substring(0, 10) === isoDate)
      const spacesTotal = inventory?.spacesTotal ?? 0
      const velocityStats = velocity.value?.get(isoDate)?.get(props.location.ID)?.get(lp.ID)

      const lpEntry: UriLpEntry = {
        lpId: lp.ID,
        velocity: velocityStats,
        spacesSold: inventory?.spacesSold ?? 0,
        spacesSoldPerChannel: inventory?.spacesSoldPerChannel ?? {},
        spacesTotal: spacesTotal,
        originalSpacesTotal: spacesTotal,
        costState: {
          id: dr.defaultRate.ID,
          defaultCost: dr.defaultRate.defaultCost,
          channelDefaultRates: [],
        },
      }

      dr.channelDefaultRates.forEach(cdr => {

        const channelFacility = lp.channelFacilities.find(cf => cf.ID === cdr.channelFacilitiesID)

        if (channelFacility) {

          const sr = dr.spotRates.find(sr => sr.channelFacilityID === channelFacility.ID && sr.startDate <= date.toString() && sr.endDate >= date.toString())

          const channelCost = sr ? sr.spotCost : cdr.channelCost

          const foo: ChannelDefaultRateFieldState = {
            id: cdr.ID,
            channelCost: channelCost,
            originalChannelCost:channelCost,
            channelFacility: channelFacility,
          }
          lpEntry.costState.channelDefaultRates.push(foo)
        }
      })

      lpEntries.push(lpEntry)

    })

    const spacesSoldPerChannel: {[key: string]: number} = {}

    inventories.value.inventory.filter(i => i.date.substring(0, 10) === isoDate).forEach(i => {

      Object.entries(i.spacesSoldPerChannel).forEach(([channelName, spaces]) => {
        if (!(channelName in spacesSoldPerChannel)) {
          spacesSoldPerChannel[channelName] = 0
        }
        spacesSoldPerChannel[channelName] += spaces
      })

    })

    const dateEntry: UriDateEntry = {
      date: date,
      disabled: disabled,
      locationSpacesSold: locationSpacesSold,
      locationSpacesRatio: locationSpacesRatio,
      spacesSoldPerChannel: spacesSoldPerChannel,
      lps: lpEntries,
    }

    return dateEntry

  })

}

const stateIsDirty = computed<boolean>(() => {

  return state.dates.some(dateEntry => {
    return dateEntry.lps.some(lpEntry => {
      const inventoryChanged = lpEntry.spacesTotal !== lpEntry.originalSpacesTotal
      if (inventoryChanged) {
        return true
      }
      const rateChanged = lpEntry.costState.channelDefaultRates.some(cdr => cdr.channelCost !== cdr.originalChannelCost)
      return rateChanged
    })
  })

})

function resetState() {

  confirmDataLoss().then(() => {
    state.dates.forEach(dateEntry => {
      dateEntry.lps.forEach(lpEntry => {
        lpEntry.spacesTotal = lpEntry.originalSpacesTotal
        const lpContext = context.value.lps.find(lpContext => lpContext.lpId === lpEntry.lpId)
        if (lpContext) {
          lpEntry.costState.defaultCost = lpContext.defaultRate.defaultCost
          lpEntry.costState.channelDefaultRates.forEach(cdrEntry => {
            const cdrContext = lpContext.defaultRate.channelDefaultRates.get(cdrEntry.channelFacility.ID)
            if (cdrContext) {
              cdrEntry.channelCost = cdrContext.channelCost
            }
          })
        }
      })
    })
  })

}

/**
 * Bulk editing
 */

function openInventoryModal(lpId: number) {

  openDialog(InventoryModal, {locationId: props.location.ID, locationProductId: lpId, context: context.value})
    .then((updated: any) => {
      // TODO: this will wipe out any fields which have been changed...
      if (updated) {
        loadDataForSelectedDateRange()
      }
    })

}

function openCostModal(lpId: number) {

  openDialog(CostModal, {locationProductId: lpId, context: context.value})
    .then((updated: any) => {
      // TODO: this will wipe out any fields which have been changed...
      if (updated) {
        loadDataForSelectedDateRange()
      }
    })

}

function openDefaultRatesModal(lpId: number) {

  openDialog(DefaultRatesModal, {locationProductId: lpId, context: context.value})
    .then((updated: any) => {
      // TODO: this will wipe out any fields which have been changed...
      if (updated) {
        loadDataForSelectedDateRange()
      }
    })

}

/**
 * Submission
 */

const submitting = ref<boolean>(false)

function submit() {

  submitting.value = true

  const inventoryUpdateRequests = buildInventoryUpdateRequests()

  const promises = inventoryUpdateRequests.map(request => {
    return Inventory.updateByLocationId(props.location.ID, request)
      .then(() => {
        // update local form state for these dates
        const numDays = request.start.until(request.end, {largestUnit: 'day'}).total('day') + 1
        for (let i = 0; i < numDays; i++) {
          const today = request.start.add({days: i})
          const dateEntry = state.dates.find(dateEntry => Temporal.PlainDate.compare(dateEntry.date, today) === 0)
          if (dateEntry) {
            request.inventories.forEach(i => {
              const lpEntry = dateEntry.lps.find(lpEntry => lpEntry.lpId === i.locationProductId)
              if (lpEntry) {
                lpEntry.spacesTotal = i.spacesTotal
                lpEntry.originalSpacesTotal = i.spacesTotal
              }
            })
          }
        }
      })
      .catch((e) => {
        console.error(e)
        NotificationService.error(`Couldn't update inventory for ${request.start.toString()} – ${request.end.toString()}!`)
        throw e
      })
  })

  // @ts-ignore
  const syncSpotRatesRequest = buildSyncSpotRatesRequest()

  if (syncSpotRatesRequest) {
    promises.push(
      RatesApi.syncSpotRates(syncSpotRatesRequest)
        .then(() => {
          // the response from the BE is kind of useless, so we can't patch state based on it. well have to rely on loadDataForSelectedDateRange().
        })
        .catch((e) => {
          console.error(e)
          NotificationService.error(`Couldn't update rates!`)
          throw e
        })
    )
  }

  Promise.all(promises)
    .then(() => {
      NotificationService.success('Updated')
      loadDataForSelectedDateRange()
    })
    .catch(() => {
      // do nothing - we already published notifications above. we're just catching here so the .finally() will run
    })
    .finally(() => {
      submitting.value = false
    })

}

function buildInventoryUpdateRequests(): UpdateInventoryByLocationIdRequest[] {

  // the state.dates map should already be in chronological order, but just in case...
  const sortedDateEntries = state.dates.sort((a, b) => Temporal.PlainDate.compare(a.date, b.date))

  const requests: UpdateInventoryByLocationIdRequest[] = []
  let pendingRequest: null | UpdateInventoryByLocationIdRequest = null

  sortedDateEntries.forEach(dateEntry => {

    const todaysInventories: UpdateInventoryByLocationIdRequest['inventories'] = []

    dateEntry.lps.forEach(lpEntry => {
      if (lpEntry.spacesTotal !== lpEntry.originalSpacesTotal) {
        // TODO: make sure lpEntry.spacesTotal is valid! (what should we do if it isn't? Notify and abort, I guess?)
        todaysInventories.push({locationProductId: lpEntry.lpId, spacesTotal: lpEntry.spacesTotal})
      }
    })

    // If we're already in the middle of building a request, determine whether we should (1) extend it to include
    // today, or (2) seal it and add it to the queue.
    if (pendingRequest) {
      const todayIsDifferent = !_.isEqual(todaysInventories, pendingRequest.inventories)
      if (todayIsDifferent) { // seal it
        requests.push(pendingRequest)
        pendingRequest = null
      } else { // extend it
        pendingRequest.end = dateEntry.date
      }
    }

    // If there are updates for today, and there is no pendingRequest (either because we just sealed one, or because
    // one didn't exist in the first place), start building a request.
    if (todaysInventories.length && !pendingRequest) {
      pendingRequest = {
        start: dateEntry.date,
        end: dateEntry.date,
        weekdays: [Weekday.Sun, Weekday.Mon, Weekday.Tue, Weekday.Wed, Weekday.Thu, Weekday.Fri, Weekday.Sat],
        inventories: todaysInventories,
      }
    }

  })

  // handle danglers
  if (pendingRequest) {
    requests.push(pendingRequest)
  }

  return requests

}

function buildSyncSpotRatesRequest(): SyncSpotRatesRequest | null {

  const items: SyncSpotRatesRequestItem[] = []

  // the state.dates map should already be in chronological order, but just in case...
  const sortedDateEntries = state.dates.sort((a, b) => Temporal.PlainDate.compare(a.date, b.date))
  const today = Temporal.Now.plainDateISO()

  sortedDateEntries.forEach((dateEntry: UriDateEntry) => {

    // don't waste CPU on past dates
    if (Temporal.PlainDate.compare(dateEntry.date, today) === -1) {
      return
    }

    dateEntry.lps.forEach((lpEntry: UriLpEntry) => {

      const lpContext = context.value.lps.find((lpContext: UriLpContext) => lpContext.lpId === lpEntry.lpId)

      if (!lpContext) {
        return
      }

      lpEntry.costState.channelDefaultRates.forEach((cdrEntry: ChannelDefaultRateFieldState) => {

        const cdrContext = lpContext.defaultRate.channelDefaultRates.get(cdrEntry.channelFacility.ID!)

        if (!cdrContext) {
          return
        }

        if (cdrEntry.channelCost && cdrEntry.channelCost !== cdrEntry.originalChannelCost) {
          items.push({
            date: dateEntry.date,
            locationProductId: lpEntry.lpId,
            defaultRateId: lpContext.defaultRate.drId,
            channelDefaultRateId: cdrEntry.id!,
            channelCost: cdrEntry.channelCost,
          })
        }
      })

    })

  })

  if (items.length) {
    const request: SyncSpotRatesRequest = {"spotRates": items}
    return request
  }

  return null

}

</script>
